/**
 * The Word Ladder Solver: the shortest ways to turn one word into another, changing a
 * single letter at a time with a real word at every step.
 *
 * Words of one length form a graph in which two words are neighbors when they differ in
 * exactly one position. To find neighbors without comparing every pair, each word is
 * filed under one "wildcard key" per position — `cold` under `?old`, `c?ld`, `co?d`,
 * `col?` — and words sharing a key are neighbors. A breadth-first search from each end
 * then gives every word's distance from both; a word lies on a shortest ladder exactly
 * when those two distances add up to the ladder length, which is how every shortest
 * ladder is counted and listed without trying paths that go nowhere.
 */

export const MIN_LENGTH = 2;
export const MAX_LENGTH = 10;

const RE_LETTERS = /^[a-z]+$/;
const RE_SEPARATORS = /[\s,]+/;
const MIN_BUCKET = 2;

type Buckets = Map<string, string[]>;

const bucketCache = new WeakMap<readonly string[], Map<number, Buckets>>();

function wildcardKey(word: string, position: number): string {
  return `${word.slice(0, position)}?${word.slice(position + 1)}`;
}

/** Words of one length filed by wildcard key; keys with a single word are dropped. */
function bucketsFor(list: readonly string[], length: number): Buckets {
  let byLength = bucketCache.get(list);
  if (!byLength) {
    byLength = new Map();
    bucketCache.set(list, byLength);
  }
  const cached = byLength.get(length);
  if (cached) {
    return cached;
  }
  const buckets: Buckets = new Map();
  for (const word of list) {
    if (word.length !== length) {
      continue;
    }
    for (let i = 0; i < length; i += 1) {
      const key = wildcardKey(word, i);
      const bucket = buckets.get(key);
      if (bucket) {
        bucket.push(word);
      } else {
        buckets.set(key, [word]);
      }
    }
  }
  for (const [key, bucket] of buckets) {
    if (bucket.length < MIN_BUCKET) {
      buckets.delete(key);
    }
  }
  byLength.set(length, buckets);
  return buckets;
}

/** Do the words differ in exactly one position? */
export function oneApart(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let differences = 0;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      differences += 1;
      if (differences > 1) {
        return false;
      }
    }
  }
  return differences === 1;
}

/** The position where two one-apart words differ, or -1. */
export function changedPosition(a: string, b: string): number {
  if (!oneApart(a, b)) {
    return -1;
  }
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) {
      return i;
    }
  }
  return -1;
}

export type LadderInput =
  | { ok: true; start: string; end: string }
  | {
      ok: false;
      reason: "empty" | "invalid" | "length" | "range";
    };

/** Two words, same length, letters only. Case and surrounding spaces are ignored. */
export function parseLadderInput(
  startText: string,
  endText: string
): LadderInput {
  const start = startText.trim().toLowerCase();
  const end = endText.trim().toLowerCase();
  if (!(start && end)) {
    return { ok: false, reason: "empty" };
  }
  if (!(RE_LETTERS.test(start) && RE_LETTERS.test(end))) {
    return { ok: false, reason: "invalid" };
  }
  if (start.length !== end.length) {
    return { ok: false, reason: "length" };
  }
  if (start.length < MIN_LENGTH || start.length > MAX_LENGTH) {
    return { ok: false, reason: "range" };
  }
  return { ok: true, start, end };
}

/** Words to avoid: split on spaces and commas, letters only, lowercase. */
export function parseExcluded(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .split(RE_SEPARATORS)
      .filter((word) => RE_LETTERS.test(word))
  );
}

export type LadderResult =
  | { kind: "same" }
  | { kind: "none"; reached: number }
  | {
      kind: "found";
      /** Letter changes needed (one fewer than the words in a ladder). */
      steps: number;
      /** How many different shortest ladders exist. */
      count: number;
      /** Up to `limit` of them, in alphabetical order. */
      ladders: string[][];
      /** Endpoints that are not in the word list. */
      unlisted: string[];
    };

/** Everything one-apart from `word` that the search may step to. */
function neighborsOf(
  word: string,
  buckets: Buckets,
  extras: ReadonlyMap<string, readonly string[]>,
  excluded: ReadonlySet<string>
): string[] {
  const found = new Set<string>(extras.get(word) ?? []);
  if (!extras.has(word)) {
    for (let i = 0; i < word.length; i += 1) {
      for (const other of buckets.get(wildcardKey(word, i)) ?? []) {
        if (other !== word) {
          found.add(other);
        }
      }
    }
    for (const extra of extras.keys()) {
      if (oneApart(word, extra)) {
        found.add(extra);
      }
    }
  }
  return [...found].filter((other) => !excluded.has(other)).sort();
}

interface Layers {
  dist: Map<string, number>;
  ways: Map<string, number>;
}

/** Breadth-first distances from `source` across the whole connected word graph. */
function explore(source: string, next: (word: string) => string[]): Layers {
  const dist = new Map<string, number>([[source, 0]]);
  const ways = new Map<string, number>([[source, 1]]);
  let frontier = [source];
  while (frontier.length > 0) {
    const upcoming: string[] = [];
    for (const word of frontier) {
      const here = dist.get(word) as number;
      for (const other of next(word)) {
        if (!dist.has(other)) {
          dist.set(other, here + 1);
          upcoming.push(other);
        }
        if (dist.get(other) === here + 1) {
          ways.set(other, (ways.get(other) ?? 0) + (ways.get(word) as number));
        }
      }
    }
    frontier = upcoming;
  }
  return { dist, ways };
}

/**
 * The shortest ladders from `start` to `end`. Both ends may be words the list lacks (the
 * ladder still treats them as endpoints); `excluded` words are never used in between.
 */
export function solveLadder(
  list: readonly string[],
  start: string,
  end: string,
  excluded: ReadonlySet<string>,
  limit: number
): LadderResult {
  if (start === end) {
    return { kind: "same" };
  }
  const buckets = bucketsFor(list, start.length);
  const listed = new Set(
    list.filter(
      (word) => word.length === start.length && (word === start || word === end)
    )
  );
  const unlisted = [start, end].filter((word) => !listed.has(word));
  // A word the list lacks has no bucket of its own, so its neighbors are found by scanning.
  const extras = new Map<string, string[]>(
    unlisted.map((word) => [
      word,
      list.filter((other) => oneApart(word, other)),
    ])
  );
  if (unlisted.length === 2 && oneApart(start, end)) {
    extras.get(start)?.push(end);
    extras.get(end)?.push(start);
  }
  const next = (word: string) => neighborsOf(word, buckets, extras, excluded);
  const fromStart = explore(start, next);
  const found = fromStart.dist.get(end);
  if (found === undefined) {
    return { kind: "none", reached: fromStart.dist.size };
  }
  const steps: number = found;
  const fromEnd = explore(end, next);
  const ladders: string[][] = [];
  const trail = [start];

  function walk(word: string, depth: number) {
    if (ladders.length >= limit) {
      return;
    }
    if (depth === steps) {
      ladders.push([...trail]);
      return;
    }
    for (const other of next(word)) {
      if (
        fromStart.dist.get(other) === depth + 1 &&
        fromEnd.dist.get(other) === steps - depth - 1
      ) {
        trail.push(other);
        walk(other, depth + 1);
        trail.pop();
      }
    }
  }

  walk(start, 0);
  return {
    kind: "found",
    steps,
    count: fromStart.ways.get(end) as number,
    ladders,
    unlisted,
  };
}
